import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { authService } from '../services/authService'
import { getThemesForEnvironment, getThemeById } from '../config/themes'
import type { TenantThemeConfig } from '../types/theme'

interface DevToolsPanelProps {
  themeConfig: TenantThemeConfig
  switchTheme: (themeId: string) => Promise<void>
  isConnected: boolean
  authType: string | null
  webSocketEnabled: boolean
  tenantId: string
  popupManagerEnabled: boolean
  currentPopup: any
  popupQueue: any[]
  popupLoading: boolean
  popupError: string | null
  disconnectWebSocket: () => void
  refreshPopups: () => void
  forceShowPopup: (popup: any) => void
}

const DevToolsPanel: React.FC<DevToolsPanelProps> = ({
  themeConfig,
  switchTheme,
  isConnected,
  authType,
  webSocketEnabled,
  tenantId,
  popupManagerEnabled,
  currentPopup,
  popupQueue,
  popupLoading,
  popupError,
  disconnectWebSocket,
  refreshPopups,
  forceShowPopup
}) => {
  const [showDevTools, setShowDevTools] = useState(false)
  const { isAuthenticated, user, token, isLoading, error, logout } = useAuth()

  const handleMockLogin = async () => {
    try {
      // Create a proper JWT-like mock token
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }))
      const payload = btoa(JSON.stringify({
        userId: 'test-user-001',
        username: 'demo',
        tenantId: authService.getTenantId(),
        role: 'user',
        permissions: ['content.read', 'chat.send'],
        exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour
        iat: Math.floor(Date.now() / 1000),
        iss: 'auth-service',
        aud: 'websocket-gateway'
      }))
      // Create a fake but valid-looking signature
      const signature = btoa('mock-signature-' + Date.now())
      const mockJWT = `${header}.${payload}.${signature}`
      
      // Create mock user data
      const mockUser = {
        _id: 'test-user-001',
        username: 'demo',
        name: 'Demo User',
        mobile: '0900000000',
        tenantId: authService.getTenantId(),
        role: 'user',
        permissions: ['content.read', 'chat.send'],
        isActive: true,
        balance: 1000,
        lastLoginAt: new Date(),
        createdAt: new Date(),
        updatedAt: new Date()
      }
      
      localStorage.setItem('token', mockJWT)
      localStorage.setItem('user', JSON.stringify(mockUser))
      localStorage.setItem('tenantId', authService.getTenantId())
      window.dispatchEvent(new CustomEvent('auth-change'))
      
      console.log('🧪 Mock JWT login triggered:', mockJWT.substring(0, 50) + '...')
    } catch (error) {
      console.error('❌ Mock login failed:', error)
    }
  }

  const handleMockLogout = async () => {
    try {
      await logout()
      console.log('🧪 Mock logout triggered')
    } catch (error) {
      console.error('❌ Mock logout failed:', error)
    }
  }

  if (!import.meta.env.DEV) {
    return null
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        className="position-fixed top-50 end-0 btn btn-dark btn-sm"
        style={{ 
          zIndex: 9999, 
          borderRadius: '8px 0 0 8px',
          transform: 'translateY(-50%)',
          padding: '8px 6px',
          fontSize: '12px'
        }}
        onClick={() => setShowDevTools(!showDevTools)}
        title={showDevTools ? 'ซ่อน Dev Tools' : 'แสดง Dev Tools'}
      >
        {showDevTools ? '›' : '‹'}
      </button>

      {/* Dev Tools Panel */}
      <div 
        className="position-fixed top-50 bg-dark text-white rounded shadow"
        style={{ 
          zIndex: 9998,
          right: showDevTools ? '0' : '-300px',
          transform: 'translateY(-50%)',
          transition: 'right 0.3s ease-in-out',
          width: '280px',
          padding: '16px',
          marginRight: showDevTools ? '16px' : '0',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <small className="fw-bold">🛠️ Dev Tools</small>
          <button 
            className="btn btn-sm btn-outline-light"
            onClick={() => setShowDevTools(false)}
            style={{ padding: '2px 6px', fontSize: '10px' }}
          >
            ✕
          </button>
        </div>
        
        {/* Theme Selector */}
        <div className="mb-3">
          <label className="form-label small">เลือกธีม:</label>
          <select 
            className="form-select form-select-sm" 
            value={themeConfig.themeId}
            onChange={(e) => switchTheme(e.target.value)}
          >
            {getThemesForEnvironment(import.meta.env.DEV)
              .filter(theme => ['huay-asia', 'huay-fast'].includes(theme.id))
              .map(theme => (
                <option key={theme.id} value={theme.id}>
                  {theme.displayName}
                </option>
              ))}
          </select>
        </div>
        
        {/* Theme Info */}
        {getThemeById(themeConfig.themeId) && (
          <div className="mb-3 p-2 bg-secondary bg-opacity-25 rounded">
            <small className="d-block mb-1">📝 {getThemeById(themeConfig.themeId)?.description}</small>
            <small className="d-block">📊 สถานะ: {getThemeById(themeConfig.themeId)?.status}</small>
          </div>
        )}
        
        {/* Current State */}
        <div className="border-top pt-2 mb-3">
          <small className="text-muted d-block">🎨 Theme: <span className="text-info">{themeConfig.themeId}</span></small>
          <small className="text-muted">🏢 Tenant: <span className="text-warning">{themeConfig.tenantId}</span></small>
        </div>
        
        {/* Smart WebSocket Status */}
        <div className="border-top pt-2 mb-3">
          <small className="fw-bold d-block mb-2">🔌 Smart WebSocket</small>
          <small className="text-muted d-block">
            📡 Status: <span className={isConnected ? 'text-success' : 'text-danger'}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </small>
          <small className="text-muted d-block">
            🔒 Mode: <span className="text-info">{authType || 'Initializing...'}</span>
          </small>
          <small className="text-muted d-block">
            ⚙️ Enabled: <span className={webSocketEnabled ? 'text-success' : 'text-danger'}>
              {webSocketEnabled ? 'Yes' : 'No'}
            </span>
          </small>
          <small className="text-muted d-block">
            🏢 Tenant: <span className="text-warning">{tenantId || 'NONE'}</span>
          </small>
          <small className="text-muted d-block">
            🎨 Theme: <span className="text-info">{themeConfig ? 'Ready' : 'Loading...'}</span>
          </small>
        </div>

        {/* Popup System Status */}
        <div className="border-top pt-2 mb-3">
          <small className="fw-bold d-block mb-2">🎪 Popup System</small>
          <small className="text-muted d-block">
            📊 Status: <span className={popupManagerEnabled ? 'text-success' : 'text-danger'}>
              {popupManagerEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </small>
          <small className="text-muted d-block">
            🎯 Current: <span className="text-info">{currentPopup?.title || 'None'}</span>
          </small>
          <small className="text-muted d-block">
            📋 Queue: <span className="text-warning">{popupQueue.length}</span>
          </small>
          {popupLoading && (
            <small className="text-muted d-block">
              ⏳ Loading: <span className="text-primary">Yes</span>
            </small>
          )}
          {popupError && (
            <small className="text-muted d-block">
              ❌ Error: <span className="text-danger">{popupError}</span>
            </small>
          )}
        </div>
        
        {/* Auth Status */}
        <div className="border-top pt-2 mb-3">
          <small className="fw-bold d-block mb-2">🔐 Auth Status</small>
          
          <div className="mb-2">
            <small className="text-muted d-block">
              Status: <span className={isAuthenticated ? 'text-success' : 'text-danger'}>
                {isAuthenticated ? 'Logged In' : 'Not Logged In'}
              </span>
              {isLoading && <span className="text-warning"> (Loading...)</span>}
            </small>
            
            {user && (
              <small className="text-muted d-block">
                User: <span className="text-info">{user.username}</span>
              </small>
            )}
            
            <small className="text-muted d-block">
              Token: <span className="text-info">
                {token ? 'Present' : 'None'}
              </span>
            </small>
            
            <small className="text-muted d-block">
              Tenant: <span className="text-warning">{authService.getTenantId()}</span>
            </small>
            
            {error && (
              <small className="text-muted d-block">
                Error: <span className="text-danger">{error}</span>
              </small>
            )}
          </div>

          {/* Auth Testing Controls */}
          <div className="border-top pt-2">
            <small className="fw-bold d-block mb-2">🧪 Auth Testing</small>
            <div className="d-grid gap-1">
              {!isAuthenticated ? (
                <button 
                  className="btn btn-sm btn-outline-success"
                  onClick={handleMockLogin}
                  disabled={isLoading}
                  style={{ fontSize: '10px' }}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Loading...
                    </>
                  ) : (
                    <>
                      🔑 Mock Login (demo/demo123)
                    </>
                  )}
                </button>
              ) : (
                <button 
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleMockLogout}
                  disabled={isLoading}
                  style={{ fontSize: '10px' }}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Logging out...
                    </>
                  ) : (
                    <>
                      🚪 Logout
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* WebSocket Controls */}
        <div className="border-top pt-2">
          <small className="fw-bold d-block mb-2">🔌 WebSocket Controls</small>
          <div className="d-grid gap-1">
            <button 
              className="btn btn-sm btn-outline-warning"
              onClick={() => {
                disconnectWebSocket();
                console.log('🚨 Emergency WebSocket disconnect');
              }}
              style={{ fontSize: '10px' }}
            >
              🚨 Force Disconnect
            </button>
          </div>
        </div>

        {/* Popup Testing Controls */}
        <div className="border-top pt-2">
          <small className="fw-bold d-block mb-2">🎪 Popup Testing</small>
          <div className="d-grid gap-1">
            <button 
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                refreshPopups();
                console.log('🔄 Manual popup refresh triggered');
              }}
              style={{ fontSize: '10px' }}
              disabled={!popupManagerEnabled}
            >
              🔄 Refresh Popups
            </button>
            <button 
              className="btn btn-sm btn-outline-warning"
              onClick={() => {
                const mockPopup = {
                  _id: 'test-popup-' + Date.now(),
                  tenantId: tenantId,
                  title: 'Test Force Show',
                  content: 'This is a test popup triggered manually for testing purposes.',
                  isActive: true,
                  priority: 100,
                  forceShow: true,
                  showOnRoutes: ['/'],
                  popupType: 'announcement' as const,
                  buttonText: 'ปิด',
                  allowDismiss: true,
                  isForceShow: true,
                  createdAt: new Date(),
                  updatedAt: new Date()
                };
                forceShowPopup(mockPopup);
                console.log('🎪 Test force show popup triggered');
              }}
              style={{ fontSize: '10px' }}
              disabled={!popupManagerEnabled}
            >
              🎪 Test Force Show
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default DevToolsPanel
